import { useEffect, useState } from "react";

export type Consent = {
  ads: boolean;
  analytics: boolean;
  date: string;
};

export type ConsentCategory = "ads" | "analytics";
export const CONSENT_EVENT = "cd-consent-change";

/** Relire le choix à chaque opération, jamais un état React potentiellement périmé. */
export function isConsentAllowed(category: ConsentCategory): boolean {
  return readConsent()?.[category] === true;
}

export function subscribeConsent(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === KEY || event.key === null) listener();
  };
  window.addEventListener(CONSENT_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CONSENT_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** A revoked session must never be resumed or replayed after later acceptance. */
export function stopRevokedTracking(): () => void {
  return subscribeConsent(() => {
    if (typeof window === "undefined") return;
    if (!isConsentAllowed("analytics")) {
      for (const key of ["cd-funnel-session-v1", "cd-funnel-state-v1", "cd-interlink-pending-v1"]) {
        try { window.sessionStorage.removeItem(key); } catch { /* unavailable */ }
      }
    }
    if (!isConsentAllowed("ads")) document.getElementById("youtube-iframe-api")?.remove();
  });
}

const KEY = "cd-consent-v1";
const EVT = "cd-consent-change";

export function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Consent>;
    if (typeof parsed?.ads !== "boolean") return null;
    return {
      ads: parsed.ads,
      analytics: parsed.analytics === true,
      date: typeof parsed.date === "string" ? parsed.date : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveConsent(value: Omit<Consent, "date">) {
  if (typeof window === "undefined") return;
  const next: Consent = { ...value, date: new Date().toISOString() };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible : le bandeau se réaffichera */
  }
  window.dispatchEvent(new CustomEvent<Consent>(EVT, { detail: next }));
}

export function resetConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVT, { detail: null }));
}

/**
 * État de consentement côté client.
 * `undefined` = pas encore lu (SSR / premier rendu) : rien de publicitaire
 * ne doit être chargé tant que ce n'est pas résolu.
 */
export function useConsent(): { consent: Consent | null; ready: boolean } {
  const [consent, setConsent] = useState<Consent | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setConsent(readConsent());
    setReady(true);
    return subscribeConsent(() => setConsent(readConsent()));
  }, []);

  return { consent, ready };
}

export function useAdsAllowed(): boolean {
  const { consent, ready } = useConsent();
  return ready && consent?.ads === true;
}

export function useAnalyticsAllowed(): boolean {
  const { consent, ready } = useConsent();
  return ready && consent?.analytics === true;
}
